import { string, addMethod } from 'yup';

/**
 * Creates a function that adds a regex valitdation method to yup.string.
 *
 * @module
 *
 * @param {object} config
 * @param {string|function} [config.defaultMessage] The default message used when the yup method creator is invoked with no message argument provided
 * @param {string} config.validationName The name of the yup method that will be created
 * @param {string} [config.readableName] The name used in the default error message; this is not needed if you provide defaultMessage
 * @param {regex} config.regex The regular expression used in the validation
 *
 * @example
 * import { createYupRegexValidationAdder } from '@somosengineering/js-util-lib';
 * import { string, object } from 'yup'
 *
 * // Using readableName
 * const addDigitsToYup = createYupRegexValidationAdder({
 *    validationName: 'digits',
 *    readableName: 'Digital Value',
 *    regex: /^\d+$/,
 * })
 *
 * // Using defaultMessage
 * const addDigits2Yup = createYupRegexValidationAdder({
 *    validationName: 'digits2',
 *    defaultMessage: ({ path }) => `${path} must contain only digits`,
 *    regex: /^\d+$/,
 * })
 *
 * // Add the method to Yup
 * addDigitsToYup();
 * addDigits2Yup();
 *
 * // Using the method in a schema
 * const schema = object({
 *    digits: string().digits(),
 *    digits2: string().digits2(),
 * })
 *
 */
const createYupRegexValidationAdder = ({
   defaultMessage: defMsg,
   validationName,
   readableName,
   regex,
}) => (
   defaultMessage = defMsg ||
      (({ path }) => `${path} must be a valid ${readableName}`)
) =>
   addMethod(string, validationName, function tspid(message = defaultMessage) {
      return this.matches(regex, {
         excludeEmptyString: true,
         message,
      });
   });

export { createYupRegexValidationAdder };
